// src/utils/perm.jsx
const MODULE_ALIAS = {
  product: "products",
  products: "products",
  exportproduct: "catalogUploads",
  exportproducts: "catalogUploads",
  catalogupload: "catalogUploads",
  cataloguploads: "catalogUploads",
  expire: "expire",
  expiredproduct: "expire",
  expiredproducts: "expire",
  brand: "brands",
  brands: "brands",
  category: "categories",
  categories: "categories",
  subcategory: "subCategory",
  "sub-categories": "subCategory",
  subcategories: "subCategory",
  unit: "units",
  units: "units",
  variantattribute: "variantAttributes",
  variantattributes: "variantAttributes",
  barcode: "barcode",
  printbarcode: "barcode",
  warranty: "warranties",
  warranties: "warranties",
  warehouse: "warehouses",
  warehouses: "warehouses",
  supplier: "suppliers",
  suppliers: "suppliers",
  customer: "customers",
  customers: "customers",
  biller: "billers",
  billers: "billers",
  order: "orders",
  orders: "orders",
  orderreport: "transactionHistory",
  orderreports: "transactionHistory",
  salesreport: "salesReport",
  purchasereport: "purchaseReport",
  inventoryreport: "inventoryReport",
  stockhistoryreport: "stockHistory",
  soldstock: "soldStockReport",
  soldstockreport: "soldStockReport",
  invoicereport: "invoiceReport",
  invoicereportnew: "invoiceReport",
  supplierreport: "supplierReport",
  supplierduereport: "supplierDueReport",
  customerreport: "customerReport",
  customerduereport: "customerDueReport",
  productreport: "productReport",
  productexpiredreport: "productExpiryReport",
  productexpiryreport: "productExpiryReport",
  productquantityreport: "productQuantityAlertReport",
  expensereport: "expenseReport",
  incomereport: "incomeReport",
  taxreport: "purchaseTaxReport",
  purchasetaxreport: "purchaseTaxReport",
  purchasetax: "purchaseTaxReport",
  profitloss: "profitLossReport",
  annualreport: "annualReport",
  salereport: "saleTaxReport",
  salestax: "saleTaxReport",
  invoice: "invoices",
  invoices: "invoices",
  transaction: "transactions",
  transactions: "transactions",
  transactionhistory: "transactionHistory",
  transactionreport: "transactionReports",
  transactionreports: "transactionReports",
  user: "users",
  users: "users",
  lowstock: "lowStock",
  lowstocks: "lowStock",
  outofstock: "outOfStock",
  outofstocks: "outOfStock",
  inventorylog: "inventoryLogs",
  inventorylogs: "inventoryLogs",
  stockhistory: "stockHistory",
  "stock-history": "stockHistory",
  stockadjustment: "stockAdjustment",
  stockadjustments: "stockAdjustment",
  stocktransfer: "stockTransfer",
  stocktransfers: "stockTransfer",
  saas: "saas",
  subscription: "saas",
  possubscription: "saas",
};

const TOKEN_KEYS = ["authToken", "token", "accessToken", "jwt"];

const normalizeModule = (moduleKey = "") => {
  const raw = String(moduleKey || "").trim();
  const compact = raw.replace(/[\s_-]+/g, "").toLowerCase();
  return MODULE_ALIAS[compact] || raw;
};

const normalizeAction = (action = "read") =>
  String(action || "read").trim().toLowerCase();

const decodeTokenPayload = () => {
  const token = TOKEN_KEYS.map((k) => localStorage.getItem(k)).find(Boolean);
  if (!token || token.split(".").length < 2) return null;
  try {
    const base64 = token
      .split(".")[1]
      .replace(/-/g, "+")
      .replace(/_/g, "/");
    const payload = JSON.parse(
      decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
          .join("")
      )
    );
    return payload || null;
  } catch {
    return null;
  }
};

const parsePermArray = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string") return item;
        if (item && typeof item === "object") {
          if (typeof item.code === "string") return item.code;
          if (item.module || item.action) {
            return `${item.module || ""}:${item.action || "read"}`;
          }
        }
        return "";
      })
      .filter(Boolean);
  }
  if (typeof value === "string") {
    return value
      .split(/[,\s]+/)
      .map((v) => v.trim())
      .filter(Boolean);
  }
  return [];
};

const readCachedPerms = () => {
  try {
    const raw = localStorage.getItem("userPermissions");
    return parsePermArray(raw ? JSON.parse(raw) : []);
  } catch {
    return [];
  }
};

const readTokenPerms = () => {
  const payload = decodeTokenPayload();
  if (!payload) return [];

  const direct =
    payload.permissions ??
    payload.perms ??
    payload.scopes ??
    payload.scope ??
    payload.authorities ??
    payload.auth;

  const nested =
    payload.user?.permissions ??
    payload.user?.perms ??
    payload.data?.permissions ??
    payload.data?.perms;

  return [...parsePermArray(direct), ...parsePermArray(nested)];
};

const normalizeCode = (code) => {
  if (!code) return null;
  const raw = String(code).trim();
  if (!raw) return null;
  if (raw === "*") return "*";

  const [moduleKey, action = "read"] = raw.split(":");
  if (!moduleKey) return null;

  const module = normalizeModule(moduleKey);
  const act = normalizeAction(action);
  return `${module}:${act}`;
};

export const getCurrentUserPerms = (userPerms = []) => {
  const merged = [
    ...parsePermArray(userPerms),
    ...readCachedPerms(),
    ...readTokenPerms(),
  ];
  return Array.from(
    new Set(
      merged
        .map(normalizeCode)
        .filter(Boolean)
    )
  );
};

export const isCurrentUserAdmin = (user = null) => {
  const payload = decodeTokenPayload();
  const role =
    user?.role ||
    localStorage.getItem("userRole") ||
    payload?.role ||
    payload?.user?.role ||
    "";
  return String(role).toLowerCase() === "admin";
};

export const hasPerm = (userPerms = [], moduleKey, action = "read") => {
  const perms = getCurrentUserPerms(userPerms);
  const module = normalizeModule(moduleKey);
  const act = normalizeAction(action);

  if (perms.includes("*")) return true;
  if (perms.includes(`${module}:*`)) return true;
  if (perms.includes(`${module}:${act}`)) return true;

  // Write/manage implies read on same module.
  if (act === "read") {
    if (perms.includes(`${module}:write`) || perms.includes(`${module}:manage`))
      return true;
  }

  // Manage implies write.
  if (act === "write" && perms.includes(`${module}:manage`)) return true;

  return false;
};

export const hasRead = (userPerms = [], moduleKey) =>
  hasPerm(userPerms, moduleKey, "read");

export const hasWrite = (userPerms = [], moduleKey) =>
  hasPerm(userPerms, moduleKey, "write");

// opts: { isAdmin?: boolean }
export const canSeeRoute = (userPerms = [], route, opts = {}) => {
  const { isAdmin = false } = opts || {};
  if (isAdmin) return true;
  const meta = route?.meta;
  if (!meta) return true;

  const defaultAction = meta.action || "read";
  const anyPerms = Array.isArray(meta.anyPerms) ? meta.anyPerms : [];

  if (anyPerms.length > 0) {
    return anyPerms.some((entry) => {
      if (typeof entry === "string") {
        return hasPerm(userPerms, entry, defaultAction);
      }
      if (entry && typeof entry === "object" && entry.perm) {
        return hasPerm(userPerms, entry.perm, entry.action || defaultAction);
      }
      return false;
    });
  }

  if (!meta.perm) return true;
  return hasPerm(userPerms, meta.perm, defaultAction);
};
